import type { IconType } from '@alpine-iq/ui-library'
import {
  DynamicDisplayTargets,
  ReviewScreenKey,
  WidgetBreakpointTab,
  WidgetChatScreen,
  WidgetRecFlowScreen,
  WidgetReviewFlowScreen,
  WidgetPriceScreen,
  WidgetReviewMyReviewsScreenSettings,
  WidgetScreen,
  WidgetSignInScreen,
  WidgetTerpenesScreen,
} from './user-settings'
import { MailingAddress } from './geo'

export const IconPresets = ['Classic', 'Smooth', '8 Bit', 'Terpli'] as const
export type IconPreset = (typeof IconPresets)[number]

export const RecFlowEffects = [
  'Calm',
  'Focused',
  'Euphoric',
  'Energized',
  'Creative',
  'Stimulated',
  'Sedated',
  'Sleepy',
  'Relaxed',
  'Uplifted',
] as const
export type RecFlowEffect = (typeof RecFlowEffects)[number]

export const ReviewFlowEffects = [
  'Calm',
  'Focused',
  'Euphoric',
  'Energized',
  'Creative',
  'Stimulated',
  'Sedated',
  'Sleepy',
  'Relaxed',
  'Uplifted',
] as const
export type ReviewFlowEffect = (typeof ReviewFlowEffects)[number]

export const RecFlowExperiences = [
  'Music',
  'Stoned',
  'Gaming',
  'Morning',
  'Exercise',
  'Creativity',
  'Sleeping',
  'Socializing',
  'Romancing',
  'Focus',
  'Mood Boost',
  'Pain Relief',
] as const
export type RecFlowExperience = (typeof RecFlowExperiences)[number]

export const ReviewFlowExperiences = [
  'Music',
  'Stoned',
  'Gaming',
  'Morning',
  'Exercise',
  'Creativity',
  'Sleeping',
  'Socializing',
  'Romancing',
  'Focus',
  'Mood Boost',
  'Pain Relief',
  //
  'Anxious',
  // 'Hungry',
  // 'Giggly',
  // 'Relief',
  // 'Paranoid',
] as const
export type ReviewFlowExperience = (typeof ReviewFlowExperiences)[number]

export const RecFlowTerpenesList = [
  'Caryophyllene',
  'Pinene',
  'Linalool',
  'Myrcene',
  'Limonene',
  'Terpinolene',
] as const
export type RecFlowTerpenes = (typeof RecFlowTerpenesList)[number]

export const RecFlowBasicEffects = ['Day', 'Balanced', 'Night'] as const
export type RecFlowBasicEffect = (typeof RecFlowBasicEffects)[number]

export const RecDosages = ['Low', 'Medium', 'High'] as const
export type RecDosage = (typeof RecDosages)[number]

// Common Types
export type MenuType = 'med' | 'rec' | 'both'
export type LocationType = 'all' | 'ecom' | 'loyalty'
export type RewardType = 'rec' | 'review'

export const EdibleSubCategories = [
  'chocolates',
  'drinks',
  'gummies',
  'tinctures',
  'tablets',
  'snacks',
  'other',
] as const
export type EdibleSubCategory = (typeof EdibleSubCategories)[number]

export const Categories = ['Flower', 'Edible', 'Preroll', 'Vape Cartridge', 'Concentrates & Extracts'] as const
export type Category = (typeof Categories)[number]

export const RecFlowTerpenesSchema = [
  'Caryophyllene',
  'Pinene',
  'Linalool',
  'Myrcene',
  'Limonene',
  'Terpinolene',
] as const
export type RecFlowTerpene = (typeof RecFlowTerpenesSchema)[number]
export const RecFlowScentSchema = ['Hoppy', 'Citrusy', 'Spicy', 'Floral', 'Earthy', 'Fruity'] as const
export type RecFlowScent = (typeof RecFlowScentSchema)[number]

export interface Coords {
  latitude: number
  longitude: number
  accuracy?: number
}

export interface PriceRange {
  min: number
  max: number
}

export interface ApiCannabinoid {
  type: string
  value: string | null
}

export interface ApiTerpene {
  name: string
  type: string
  presence: number | null
  weight: number | null
}

export interface ApiPrice {
  price: number | null
  weight: string | null
  discounted_price: number | null
}

// These fields should maintain the same from retialer to retailer
//
// THCA and cannabinoids are optional because the widgetapi pending-reviews
// enrichment (see api PR #8494) returns strain / THC / CBD / terpenes / prices
// / sponsored / product_url / imageURL from the terpli retail catalog but not
// THCA or the full cannabinoid list. Recommendation-flow endpoints still
// populate them.
export interface Product {
  product_uid: string
  strain: string
  brand: string
  category: Category
  THC: string | null
  CBD: string | null
  THCA?: string | null
  cannabinoids?: ApiCannabinoid[]
  terpenes: ApiTerpene[]
}

// Each of these can change by retailer.
export interface ProductView extends Product {
  sponsored: boolean
  prices: ApiPrice[]
  product_url: string | null
  imageURL: string
  dosage?: string
}

/**
 * The server currently serializes `reviewComment` via `bigquery.NullString`
 * and `createdAt` / `updatedAt` via `bigquery.NullTimestamp`, both of which
 * marshal on the wire as `{ StringVal | Timestamp, Valid }`. Read them via
 * the `readNullString` / `readNullTimestamp` helpers below. When BE
 * normalizes these to plain nullable strings, collapse the wrappers back to
 * `string | null`.
 */
export interface TerpliBqNullString {
  StringVal: string
  Valid: boolean
}

export interface TerpliBqNullTimestamp {
  Timestamp: string
  Valid: boolean
}

export function readNullString(v: TerpliBqNullString | null | undefined): string {
  return v?.Valid ? v.StringVal : ''
}

export function readNullTimestamp(v: TerpliBqNullTimestamp | null | undefined): string {
  return v?.Valid ? v.Timestamp : ''
}

export interface ReviewedProductView extends ProductView {
  rating: number
  effects: ReviewFlowEffect[]
  positiveExperiences: RecFlowExperience[]
  negativeExperiences: RecFlowExperience[]
  potency: number
  reviewComment: TerpliBqNullString | null
  createdAt: TerpliBqNullTimestamp
  updatedAt?: TerpliBqNullTimestamp
  retailName?: string
}

export interface EdibleProductView extends ProductView {
  effect: string
  dosage: string
}

// Recommendations Responses
export interface InhalableRecommendationResponse {
  products: ProductView[]
  recommendationId: string
}

export interface EdibleRecommendationResponse {
  products: EdibleProductView[]
  recommendationId: string
}

export interface ApiResponse<T> {
  data: T
  code?: number
  success?: boolean
}

export interface ApiErrorResponse {
  errors?: Array<{ message: string }>
  data?: {
    error: string
    message: string
  }
  code?: number
  success: false
}

export interface SubEcomLocation {
  menuType: MenuType
  menuProviderID?: string
  availableCategories: Category[]
  edibleSubCategories: EdibleSubCategory[]
  dynamicDisplayTargets: DynamicDisplayTargets
  ecomMenuProvider?: string
}

export interface EcomLocation {
  uid: string
  globalID: string
  storeID: string
  locationName: string
  subEcomLocations: SubEcomLocation[]
  address?: MailingAddress
  distanceInMiles: number
  ecomMenuProviders?: string[]
}

export interface LoyaltyLocation {
  uid: string
  locationName: string
  dynamicDisplayTargets: DynamicDisplayTargets
}

export interface LocationsResponse {
  ecomLocations: EcomLocation[]
  loyaltyLocations: LoyaltyLocation[]
  enabled?: boolean
  code: number
  success: boolean
  ecomMenuProviders?: string[]
}

export interface LocationsRequest {
  uids: string[]
  coords?: Coords
  preview?: boolean
}

// Analytics Types
export interface AnalyticsEventRequest {
  uids: string[]
  event_type: string
  action: string
  details: Record<string, any>
  deviceId: string
  url: string
  sessionId: string
  iframeURL?: string
  os: string
  deviceClass?: string
  browser?: string
  userAgent?: string
  mode: string
  ecomPlatform?: string
  route: string
  contactID?: string
  source?: string
}

// Sign-In Types
export interface SignInStateResponse {
  signupLink: string
}

// Reviews Auth Types
//
// The Reviews feature uses its own OTP + JWT flow, separate from the legacy
// (now-removed) /widget/signin/* endpoints. See widgetapi PR #8455 for the
// server contract:
//   POST /widget/reviews/auth/send/:uid/:pii     -> SendReviewCodeResponse
//   POST /widget/reviews/auth/verify/:uid/:pii   -> ReviewAuthTokenResponse
//   POST /widget/reviews/auth/refresh            -> ReviewAuthTokenResponse
export type ReviewAuthChannel = 'sms' | 'email'

export interface SendReviewCodeRequest {
  channel?: ReviewAuthChannel
}

export interface SendReviewCodeResponse {
  sent: boolean
  channel: string
  expiresAt: string
}

export interface VerifyReviewCodeRequest {
  code: string
}

export interface ReviewAuthTokenResponse {
  token: string
  expiresAt: string
}

// Chat Types
// `role` is intentionally a wide string type. The astro-shopper backend may
// return shopper turns as 'user', 'shopper', 'customer', or 'human', and
// store-side turns as 'assistant', 'system', or 'merchant' (Chat Inbox).
// Use isAssistantMessage() in useChatStore rather than string-comparing role.
export interface ChatMessage {
  id?: string
  role: string
  content: string
  createdAt?: string
  /** Client-only: optimistic send failed and can be retried. */
  sendFailed?: boolean
}

export interface ChatSession {
  id: string
  title?: string
  status?: string
  created?: string
  updated?: string
  lastMessageAt?: string
}

/**
 * Handle returned by POST /messages when the shopper backend has a live
 * WebSocket stream ready for this job. Absent on older backends or when the
 * stream broker is unavailable — callers must handle undefined by falling
 * back to HTTP polling.
 */
export interface ChatStreamHandle {
  path: string
  wsAuthTicket: string
  jobID?: string
}

export interface ChatAiReplyJob {
  id: string
  sessionID?: string
  status?: string
  error?: string
}

export interface PostChatMessageResult {
  stream?: ChatStreamHandle
  message?: ChatMessage
  session?: ChatSession
  job?: ChatAiReplyJob
}

// Reviews Types

/**
 * A product surfaced in the pending reviews list. Extends ProductView because
 * the backend enriches responses with the same product-detail fields the widget
 * uses everywhere else (strain, brand, imageURL, prices, THC/CBD, etc.).
 */
/**
 * The server (widgetapi PR #8494) enriches pending review products with the
 * same fields ProductView carries: strain, imageURL, THC, CBD, terpenes,
 * prices, sponsored, product_url, plus brand and category. It does NOT
 * populate `product_uid` (that field is FE-native); it sends `productId`
 * instead. Run each pending product through `hydratePendingReviewProduct`
 * on ingestion so `product_uid` is populated for keying, analytics, and
 * submit payloads.
 */
export interface PendingReviewProduct extends ProductView {
  productId: string
  productName: string
  purchasedAt: string
  transactionId?: string
}

/**
 * Fill `product_uid` from `productId` and provide safe defaults for the
 * fields BE doesn't currently return (THCA, cannabinoids, dosage). Idempotent.
 */
export function hydratePendingReviewProduct(p: PendingReviewProduct): PendingReviewProduct {
  return {
    ...p,
    product_uid: p.product_uid || p.productId,
    // Prefer strain when populated; some legacy tenants only set productName.
    strain: p.strain || p.productName,
    // Rec-flow-only fields BE doesn't return here — default so consumers don't
    // have to null-check on every access.
    THCA: p.THCA ?? null,
    cannabinoids: p.cannabinoids ?? [],
    terpenes: p.terpenes ?? [],
    prices: p.prices ?? [],
    product_url: p.product_url ?? null,
  }
}

/**
 * Backend metadata that accompanies the pending list. Exposed so screens can
 * surface things like "earn X points per review, up to Y per day" without
 * needing a separate settings fetch.
 */
export interface PendingReviewRewards {
  type: string
  pointsPerReview?: {
    enabled: boolean
    points: number
    maxPerShopperPerDay?: number
    unlimited: boolean
  }
}

export interface PendingReviewsResponse {
  products: PendingReviewProduct[]
  lookbackHours: number
  reviewRewards: PendingReviewRewards
}

/**
 * A stored review record. Extends ReviewedProductView so screens can render
 * the same shape used elsewhere. `pointsAwarded` is present on submit
 * responses (synchronous grant) and may be absent on list responses.
 */
export interface TerpliProductReview extends ReviewedProductView {
  reviewId: string
  userId: string
  productId: string
  contactId?: string
  email?: string
  phone?: string
  thirdPartyCustomerId?: string
  pointsAwarded?: number
}

/**
 * Same hydration pattern as `hydratePendingReviewProduct`: fill FE-native
 * `product_uid` from BE `productId` so keys and analytics work, and default
 * the rec-only fields BE doesn't return on completed responses today.
 * When BE enriches completed responses too (follow-up to api PR #8494),
 * this becomes idempotent and no-op for populated fields.
 */
const DEFAULT_PRODUCT_FALLBACK_IMAGE = 'https://dispense-images.imgix.net/default-flower.png'

export function hydrateTerpliProductReview(r: TerpliProductReview): TerpliProductReview {
  return {
    ...r,
    product_uid: r.product_uid || r.productId,
    strain: r.strain || '',
    brand: r.brand || '',
    category: (r.category || '') as Category,
    THCA: r.THCA ?? null,
    cannabinoids: r.cannabinoids ?? [],
    terpenes: r.terpenes ?? [],
    prices: r.prices ?? [],
    product_url: r.product_url ?? null,
    imageURL: r.imageURL || DEFAULT_PRODUCT_FALLBACK_IMAGE,
  }
}

export type CompletedReviewsResponse = TerpliProductReview[]

export interface UserReviewForm {
  rating: number
  effects: string[]
  positiveExperiences: string[]
  negativeExperiences: string[]
  potency?: number
  reviewComment: string
}

export interface SubmitReviewRequest {
  productId: string
  rating: number
  effects: string[]
  positiveExperiences: string[]
  negativeExperiences: string[]
  potency?: number
  reviewComment?: string | null
}

export type SubmitReviewResponse = TerpliProductReview

export interface InhalableFilters {
  category?: string[]
  effects?: string[]
  price?: PriceRange
  strength?: number
  terpenes?: string[]
}

export interface EdibleFilters {
  category?: Category[]
  edibleCategory?: string[]
  basicEffect?: string
  dosage?: string
}

export interface ScentsFilters {
  category?: Category[]
  scent?: string
  price?: PriceRange
  strength?: number
}

export type RecommendationFilters = InhalableFilters | EdibleFilters | ScentsFilters
export type CombinedRecommendationFilters = InhalableFilters & EdibleFilters & ScentsFilters

export interface RecommendationRequest {
  deviceId: string
  contactID?: string
  filters: RecommendationFilters
}

export interface ApplyRewardRequest {
  rewardType: RewardType
  contactId?: string
  phone?: string
  email?: string
  details?: {
    reviewId?: string
  }
}

export interface RewardState {
  points: number
  userEligible: boolean
}

export interface RewardsEligibility {
  isEligible: boolean
  message: string
}

export interface RewardsStateResponse {
  signupLink: string | null
  loyaltyProgramName: string
  review?: RewardState
  rec?: RewardState
  eligibility?: RewardsEligibility
}

export enum WidgetStep {
  // Universal steps
  HowItWorks = 'howItWorks',
  SignInPage = 'signInPage',
  ProductDetails = 'productDetails',
  SelectStore = 'selectStore',
  SelectMenuType = 'selectMenuType',
  AboutTerpenes = 'aboutTerpenes',

  // Recommendations flow steps
  Welcome = 'welcome',
  Category = 'category',
  SubCategory = 'subCategory',
  Effects = 'effects',
  Potency = 'potency',
  Price = 'price',
  Terpenes = 'terpenes',
  RecSignInPage = 'recSignInPage',
  WalletPass = 'walletPass',
  Recommendations = 'recommendations',

  // Reviews flow steps
  ReviewSignInPage = 'reviewSignInPage',
  ReviewStars = 'reviewStars',
  ReviewEffects = 'reviewEffects',
  ReviewPairedGoodWith = 'reviewPairedGoodWith',
  ReviewPairedBadWith = 'reviewPairedBadWith',
  ReviewPotency = 'reviewPotency',
  ReviewComments = 'reviewComments',
  ReviewClaimPoints = 'reviewClaimPoints',
  ReviewComplete = 'reviewComplete',

  // Branch off from flow stepsds
  ReviewProductList = 'reviewProductList',
  ReviewMyReviews = 'reviewMyReviews',

  // Chat flow steps
  Chat = 'chat',
}

/**
 * Maps each WidgetStep to its specific settings type.
 * Used for type-safe settings access in hooks and components.
 */
export type StepSettingsMap = {
  // Universal steps
  [WidgetStep.HowItWorks]: WidgetScreen
  [WidgetStep.SignInPage]: WidgetSignInScreen
  [WidgetStep.AboutTerpenes]: WidgetScreen

  // Recommendation flow steps
  [WidgetStep.Welcome]: WidgetScreen
  [WidgetStep.SelectStore]: WidgetScreen
  [WidgetStep.SelectMenuType]: WidgetScreen
  [WidgetStep.Category]: WidgetRecFlowScreen
  [WidgetStep.SubCategory]: WidgetRecFlowScreen
  [WidgetStep.Effects]: WidgetRecFlowScreen
  [WidgetStep.Potency]: WidgetRecFlowScreen
  [WidgetStep.Price]: WidgetPriceScreen
  [WidgetStep.Terpenes]: WidgetTerpenesScreen
  [WidgetStep.RecSignInPage]: WidgetSignInScreen
  [WidgetStep.WalletPass]: WidgetScreen
  [WidgetStep.Recommendations]: WidgetScreen

  // Review flow steps
  [WidgetStep.ReviewSignInPage]: WidgetSignInScreen
  [WidgetStep.ReviewStars]: WidgetReviewFlowScreen
  [WidgetStep.ReviewEffects]: WidgetReviewFlowScreen
  [WidgetStep.ReviewPairedGoodWith]: WidgetReviewFlowScreen
  [WidgetStep.ReviewPairedBadWith]: WidgetReviewFlowScreen
  [WidgetStep.ReviewPotency]: WidgetReviewFlowScreen
  [WidgetStep.ReviewComments]: WidgetReviewFlowScreen
  [WidgetStep.ReviewClaimPoints]: WidgetScreen
  [WidgetStep.ReviewComplete]: WidgetScreen
  [WidgetStep.ReviewProductList]: WidgetScreen
  [WidgetStep.ReviewMyReviews]: WidgetReviewMyReviewsScreenSettings
  [WidgetStep.ProductDetails]: WidgetScreen

  // Chat flow steps
  [WidgetStep.Chat]: WidgetChatScreen
}

export type WidgetQuestionField =
  // Add more as they are added
  WidgetStep.Category | WidgetStep.Effects | WidgetStep.Potency | WidgetStep.Price | WidgetStep.Terpenes

export enum WidgetFlow {
  Recommendation = 'recommendation',
  ProductReview = 'productReview',
  MyReviews = 'myReviews',
  Chat = 'chat',
}

export type WidgetBreakPointInfo = {
  label: string
  icon?: IconType
  breakpointWidth?: number
}

export const BreakpointTabs: Record<WidgetBreakpointTab, WidgetBreakPointInfo> = {
  all: {
    label: 'All',
    icon: 'BROWSER',
    breakpointWidth: 9999999,
  },
  desktop: {
    label: 'Desktop',
    icon: 'BROWSER',
    breakpointWidth: 1024,
  },
  tablet: {
    label: 'Tablet',
    icon: 'MENU',
    breakpointWidth: 600,
  },
  phone: {
    label: 'Phone',
    icon: 'MOBILE',
    breakpointWidth: 0,
  },
}

export enum CustomEffect {
  GlowEffect = 'glowEffect',
  BounceEffect = 'bounceEffect',
  PeakingEffect = 'peakingEffect',
}

export type CustomEffectInfo = {
  label: string
  description: string
}

export const CustomEffects: Record<CustomEffect, CustomEffectInfo> = {
  glowEffect: {
    label: 'Glow Effect',
    description: 'Gently glows to attract attention to the widget',
  },
  bounceEffect: {
    label: 'Bounce Effect',
    description: 'Quick, subtle bounce animation',
  },
  peakingEffect: {
    label: 'Peaking Effect',
    description: 'Hides widget on edge of screen, until user engages with it',
  },
}

export type WidgetQuestionInfo = {
  label: string
  defaultPriority?: number
  enabled?: boolean // Default enabled value for the inhalable flow
  required?: boolean // Default required value for the inhalable flow
  edibleEnabled?: boolean // Default enabled value for the edibles flow
  edibleRequired?: boolean // Default required value for the edibles flow
  hasSpecialConfig?: boolean // If true, the field has a special config
}

export const WidgetQuestionFields: Record<WidgetQuestionField, WidgetQuestionInfo> = {
  [WidgetStep.Category]: {
    label: 'Category',
    enabled: true,
    required: true,
    edibleEnabled: true,
    edibleRequired: true,
    defaultPriority: 0,
    hasSpecialConfig: true,
  },
  [WidgetStep.Effects]: {
    label: 'Effects',
    enabled: true,
    required: true,
    edibleEnabled: true,
    edibleRequired: true,
    defaultPriority: 1,
  },
  [WidgetStep.Potency]: {
    label: 'Potency',
    enabled: true,
    edibleEnabled: true,
    edibleRequired: true,
    defaultPriority: 2,
  },
  [WidgetStep.Terpenes]: {
    label: 'Terpenes',
    enabled: true,
    edibleEnabled: false,
    defaultPriority: 3,
    hasSpecialConfig: true,
  },
  [WidgetStep.Price]: {
    label: 'Price',
    enabled: true,
    edibleEnabled: false,
    defaultPriority: 4,
  },
}

export type WidgetReviewQuestionField =
  | WidgetStep.ReviewStars
  | WidgetStep.ReviewEffects
  | WidgetStep.ReviewPairedGoodWith
  | WidgetStep.ReviewPairedBadWith
  | WidgetStep.ReviewPotency
  | WidgetStep.ReviewComments

export type WidgetReviewQuestionInfo = {
  label: string
  screenKey: ReviewScreenKey
  defaultPriority: number
  defaultEnabled: boolean
  defaultRequired: boolean
  canBeDisabled: boolean
  hasSpecialConfig?: boolean
}

export const WidgetReviewQuestionFields: Record<WidgetReviewQuestionField, WidgetReviewQuestionInfo> = {
  [WidgetStep.ReviewStars]: {
    label: 'Rating',
    screenKey: 'stars',
    defaultEnabled: true,
    defaultRequired: true,
    defaultPriority: 0,
    canBeDisabled: false,
  },
  [WidgetStep.ReviewEffects]: {
    label: 'Effects',
    screenKey: 'effects',
    defaultEnabled: true,
    defaultRequired: true,
    defaultPriority: 1,
    canBeDisabled: false,
  },
  [WidgetStep.ReviewPairedGoodWith]: {
    label: 'Good Experiences',
    screenKey: 'goodExperience',
    defaultEnabled: true,
    defaultRequired: true,
    defaultPriority: 2,
    canBeDisabled: false,
  },
  [WidgetStep.ReviewPairedBadWith]: {
    label: 'Bad Experiences',
    screenKey: 'badExperience',
    defaultEnabled: true,
    defaultRequired: false,
    defaultPriority: 3,
    canBeDisabled: true,
  },
  [WidgetStep.ReviewPotency]: {
    label: 'Potency',
    screenKey: 'potency',
    defaultEnabled: true,
    defaultRequired: false,
    defaultPriority: 4,
    canBeDisabled: true,
  },
  [WidgetStep.ReviewComments]: {
    label: 'Comments',
    screenKey: 'comment',
    defaultEnabled: true,
    defaultRequired: false,
    defaultPriority: 5,
    canBeDisabled: true,
  },
}

export const REVIEW_FLOW_STEPS: WidgetReviewQuestionField[] = [
  WidgetStep.ReviewStars,
  WidgetStep.ReviewEffects,
  WidgetStep.ReviewPairedGoodWith,
  WidgetStep.ReviewPairedBadWith,
  WidgetStep.ReviewPotency,
  WidgetStep.ReviewComments,
]

export type ReviewScreenDisplayRow = {
  step: WidgetReviewQuestionField
  screenKey: ReviewScreenKey
  label: string
  enabled: boolean
  required: boolean
  priority: number
  hasSpecialConfig?: boolean
  canBeDisabled: boolean
}

export function getReviewCanBeDisabled(step: WidgetReviewQuestionField): boolean {
  return WidgetReviewQuestionFields[step].canBeDisabled
}

export function getReviewFlowSteps(): WidgetReviewQuestionField[] {
  return REVIEW_FLOW_STEPS
}

export const getDefaultReviewFlowQuestionScreenSettings = (): Record<string, WidgetReviewFlowScreen> => {
  const fields = Object.entries(WidgetReviewQuestionFields) as [
    WidgetReviewQuestionField,
    WidgetReviewQuestionInfo,
  ][]
  const questions: Record<string, WidgetReviewFlowScreen> = {}

  fields.forEach(([, metaData]) => {
    questions[metaData.screenKey] = {
      enabled: metaData.defaultEnabled,
      required: metaData.defaultRequired,
      priority: metaData.defaultPriority,
    }
  })

  return questions
}

export const getDefaultRecFlowQuestionScreenSettings = (): Record<string, WidgetRecFlowScreen> => {
  const fields = Object.entries(WidgetQuestionFields) as [WidgetQuestionField, WidgetQuestionInfo][]
  const questions: Record<string, WidgetRecFlowScreen> = {}

  fields.forEach(([field, metaData]) => {
    const fieldKey = field.toString().replace(/([A-Z])/g, (match) => match.toLowerCase())
    questions[fieldKey] = {
      enabled: metaData.enabled ?? false,
      required: metaData.required ?? false,
      priority: metaData.defaultPriority ?? 0,
    }
  })

  return questions
}

// Helpert to get the nums as an actual [array] of strings
export const getRecFlowEffectsSchemaArray = () => Array.from(RecFlowEffects)
export const getReviewFlowEffectsSchemaArray = () => Array.from(ReviewFlowEffects)
export const getRecFlowTerpenesSchemaArray = () => Array.from(RecFlowTerpenesList)
export const getRecFlowScentSchemaArray = () => Array.from(RecFlowScentSchema)
export const getRecDosageSchemaArray = () => Array.from(RecDosages)
export const getRecFlowBasicEffectSchemaArray = () => Array.from(RecFlowBasicEffects)
export const getRecFlowExperienceSchemaArray = () => Array.from(RecFlowExperiences)
export const getReviewFlowExperienceSchemaArray = () => Array.from(ReviewFlowExperiences)

export const TERPENE_MAPPING: Record<RecFlowTerpene, string> = {
  Caryophyllene: 'Peppery, pain relief',
  Pinene: 'Pine notes, aids focus',
  Linalool: 'Lavender, relaxes',
  Myrcene: 'Hoppy, sedative',
  Limonene: 'Citrus, uplifting',
  Terpinolene: 'Fruity, energizing',
}

export const SCENT_MAPPING: Record<RecFlowScent, RecFlowTerpene> = {
  Hoppy: 'Myrcene',
  Citrusy: 'Limonene',
  Spicy: 'Caryophyllene',
  Floral: 'Linalool',
  Earthy: 'Pinene',
  Fruity: 'Terpinolene',
}
